
import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import MenuBar from './editor/MenuBar';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import VideoExtension from './editor/extensions/VideoExtension';
import { useOrganizationId } from '../hooks/useOrganizationId';

interface ProductDescriptionProps {
  description: string;
  setDescription: (value: string) => void;
  shopId?: string;
}

/**
 * Fonction pour désencoder les caractères HTML échappés si nécessaire
 */
const decodeHtmlEntities = (html: string): string => {
  if (!html) return '';
  
  // Vérifier si le HTML est échappé
  const isEscaped = html.includes('&lt;') || html.includes('&gt;') || html.includes('&quot;') || html.includes('&amp;');
  
  if (isEscaped) {
    console.log("HTML échappé détecté, désencodage en cours...");
    const textarea = document.createElement('textarea');
    textarea.innerHTML = html;
    return textarea.value;
  }
  
  return html;
};

/**
 * Vérifier si le HTML contient un iframe pour déboguer
 */
const containsIframe = (html: string): boolean => {
  return html.includes('<iframe') || html.includes('&lt;iframe');
};

const ProductDescription = ({
  description,
  setDescription,
  shopId
}: ProductDescriptionProps) => {
  const { organizationId } = useOrganizationId();
  const contentUpdatedRef = useRef(false);
  
  // Désencode les caractères HTML échappés
  const decodedDescription = React.useMemo(() => {
    const decoded = decodeHtmlEntities(description || '');
    
    console.log("Description originale reçue:", description);
    console.log("Description après désencodage:", decoded);
    console.log("Contient un iframe:", containsIframe(description || ''));
    console.log("Organization ID pour l'upload d'images:", organizationId);
    
    return decoded;
  }, [description]);
  
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        }
      }),
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-500 underline'
        }
      }),
      Image.configure({
        inline: false,
        allowBase64: true,
        HTMLAttributes: {
          class: 'max-w-full rounded-md my-4',
        },
      }),
      VideoExtension.configure({
        HTMLAttributes: {
          class: 'my-4',
        },
      }),
      TextStyle,
      Color
    ],
    content: decodedDescription || '',
    onUpdate: ({ editor }) => {
      const html = editor.getHTML();
      console.log("HTML généré par l'éditeur:", html);
      console.log("Contient un iframe après mise à jour:", containsIframe(html));
      
      // Éviter les boucles infinies en ne mettant à jour que si le contenu a changé
      if (decodedDescription !== html) {
        setDescription(html);
      }
    },
    editorProps: {
      attributes: {
        class: 'focus:outline-none min-h-[300px] prose prose-sm max-w-none px-4 py-2 dark:text-gray-200'
      },
      transformPastedHTML(html) {
        console.log("HTML collé avant transformation:", html);
        // Désencoder si nécessaire
        const decodedHtml = decodeHtmlEntities(html);
        console.log("HTML collé après désencodage:", decodedHtml);
        return decodedHtml;
      },
      handleDOMEvents: {
        keydown: () => false,
        paste: () => false,
      },
    },
    autofocus: 'end',
    enableInputRules: true, 
    enablePasteRules: true,
    enableCoreExtensions: true,
  });
  
  // Handle content updates from outside
  useEffect(() => {
    if (!editor || !decodedDescription || contentUpdatedRef.current) return;
    
    // Ne mettre à jour que si le contenu a réellement changé
    if (editor.getHTML() !== decodedDescription) {
      contentUpdatedRef.current = true;
      
      try {
        console.log("Setting editor content:", decodedDescription);
        
        // Mise à jour différée du contenu
        const timer = setTimeout(() => {
          editor.commands.setContent(decodedDescription);
          // Réinitialiser le drapeau après un délai pour permettre les mises à jour futures
          setTimeout(() => {
            contentUpdatedRef.current = false;
          }, 100);
        }, 50);
        
        return () => clearTimeout(timer);
      } catch (error) {
        console.error("Error setting editor content:", error);
        contentUpdatedRef.current = false;
      }
    }
  }, [decodedDescription, editor]);

  return (
    <Card className="shadow-sm border border-gray-200 dark:border-gray-800">
      <CardHeader className="pb-0">
        <CardTitle className="text-lg font-medium dark:text-gray-200">Description du produit</CardTitle>
      </CardHeader>
      <CardContent className="pt-4">
        <div className="border rounded-md overflow-hidden bg-white dark:bg-gray-800/50 dark:border-gray-700">
          <MenuBar editor={editor} shopId={shopId} organizationId={organizationId} />
          <div className="relative">
            <EditorContent 
              editor={editor} 
              className="min-h-[300px] prose prose-sm max-w-none bg-white dark:bg-gray-800/50 dark:text-gray-200 video-editor"
            />
            {editor && !editor.getText() && (
              <div className="absolute inset-0 pointer-events-none px-4 py-4 text-gray-400 dark:text-gray-500">
                Commencez à saisir la description du produit...
              </div>
            )}
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
          Le contenu HTML riche est stocké directement dans la base de données
        </p>
      </CardContent>
    </Card>
  );
};

export default ProductDescription;
